import PropTypes from 'prop-types';
import React from 'react';

import APIErrorBoundary from '@ui/API/ErrorBoundary';

// In order for us to generate uniquely identifable and repeatable error codes that can be sent to us by customers and
// referenced in Sentry we need to hash error messages.
//
// https://gist.github.com/hyamamoto/fd435505d29ebfa3d9716fd2be8d42f0
function hashErrorMessageToUniqueCode(string) {
  let uniqueCode = 0;
  for (let i = 0; i < string.length; i += 1) {
    uniqueCode = Math.imul(31, uniqueCode) + string.charCodeAt(i) || 0;
  }

  // If for whatever reason we couldn't create an error code for this, create a non-zero random number.
  if (uniqueCode === 0) {
    uniqueCode = Math.random().toString(36).substr(2, 7);
  } else {
    uniqueCode = uniqueCode.toString(36).replace(/-/g, '');
  }

  return uniqueCode.toUpperCase();
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: false,
      errorCode: false,
      info: false,
    };

    // For instances where we're running async processes we can't rely on `componentDidCatch` to catch errors because
    // it doesn't catch errors that are thrown from React hooks or inside of `componentDidMount` so we're going to be
    // passing in the `error` prop.
    //
    // @link https://github.com/facebook/react/issues/11334
    if (props.error) {
      this.state.error = props.error;
      this.state.errorCode = this.getSupportCodeAndDispatchError(props.error, false);
    }
  }

  getSupportCodeAndDispatchError(error, info) {
    const supportErrorCode = `ERR-${hashErrorMessageToUniqueCode(typeof error === 'string' ? error : error.message)}`;
    const errorData = {
      supportErrorCode,
      componentStack: info,
      ...error,
    };

    if (this.props.onError) {
      this.props.onError(error, errorData);
    }

    return supportErrorCode;
  }

  componentDidCatch(error, info) {
    this.setState({
      error,
      errorCode: this.getSupportCodeAndDispatchError(error, info),
      info,
    });
  }

  render() {
    const { error, errorCode } = this.state;
    const { appContext, children, maskErrorMessages } = this.props;

    if (!error) {
      return children;
    }

    function getErrorMessage() {
      let msg;
      if (appContext === 'endpoint') {
        msg = "This endpoint's documentation is currently experiencing difficulties and will be back online shortly.";
      } else {
        msg = 'This reference guide is currently experiencing difficulties and will be back online shortly.';
      }

      if (maskErrorMessages) {
        return msg;
      }

      return (
        <span>
          {msg} Please contact{' '}
          <a href={`mailto:support@readme.io?subject=API Explorer Error [${errorCode}]`}>support@readme.io</a> with your
          error code.
        </span>
      );
    }

    return <APIErrorBoundary errorCode={errorCode} errorMessage={getErrorMessage()} padding="lg" />;
  }
}

ErrorBoundary.propTypes = {
  appContext: PropTypes.oneOf(['endpoint', 'explorer']).isRequired,
  children: PropTypes.node.isRequired,
  error: PropTypes.any,
  maskErrorMessages: PropTypes.bool,
  onError: PropTypes.func,
};
